import Styles from 'blaze-components/MobileWriterTopBar/MobileWriterTopBar.module.scss'
import { observer } from 'mobx-react-lite'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { AlmanacFileTitle } from 'common/Breadcrumbs/AlmanacFileBreadcrumbs'
import { ShareButtonButtonWithPlaceholder } from 'blaze-components/WriterTopBar/WriterTopBar'
import { TryBlazeButton } from 'blaze-components/WriterTopBar/TryBlazeButton'
import { CanView } from 'components/Can'
import { ArticleMeta, ArticleView, UserRole, Workspace } from 'types'
import { PermittedArticleViewProvider } from 'providers/PermittedArticleViewProvider/PermittedArticleViewProvider'
import { DocMenuDrawer } from 'blaze-components/MobileWriterTopBar/DocMenuDrawer'
import { PropsWithChildren } from 'react'
import { useArticleView } from 'providers/ArticleViewProvider/ArticleViewProvider'
import { useRootStore } from 'stores/RootStore'
import { DocStoreContext } from 'stores/DocStore'
import { CollaborationDrawerTrigger } from './CollaborationDrawerTrigger'
import { useWorkspace } from 'hooks/useWorkspace'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { SCHEDULED_POST_MOBILE_TOP_BAR } from 'components/ArticlePostPreview/PostPreviewBody/TopBar/TopBar'
import { MoreDots } from 'blaze-ui/icons'
import { MobileTopBarContainer } from 'blaze-components/MobileTopBarContainer'
import { ScheduledPostStatusTag } from 'components/ScheduledPostStatusTag/ScheduledPostStatusTag'
import { TopBarButton } from 'blaze-components/WriterTopBar/TopBarButton'

const PermittedViewContainer = ({
  children,
  articleMeta,
  targetArticleView = ArticleView.Editing,
}: PropsWithChildren<{ articleMeta: ArticleMeta; targetArticleView?: ArticleView }>) => {
  const { can_comment = false, can_edit_content } = articleMeta.almanac_file.permissions
  return (
    <PermittedArticleViewProvider targetArticleView={targetArticleView} permissions={{ can_comment, can_edit_content }}>
      {children}
    </PermittedArticleViewProvider>
  )
}

interface Props {
  workspace: Workspace
}

export const MobileWriterTopBar = observer(({ workspace }: Props) => {
  const articleMeta = useCurrentArticleMeta()
  const scheduledPostId = articleMeta?.almanac_file.scheduled_post_id
  const { docStore, drawersStore } = useRootStore()
  const articleView = useArticleView()
  const articleWorkspace = useWorkspace(articleMeta?.company_id || null) || { role: UserRole.UNAUTHED }
  const isUnauthed = [UserRole.UNAUTHED].flat().includes(articleWorkspace.role)
  const { isChannelPostable } = useIsChannelPostable()

  const isPostable = articleMeta && isChannelPostable(articleMeta.channel)

  return (
    <>
      {isPostable && <div id={SCHEDULED_POST_MOBILE_TOP_BAR} className={Styles.scheduledPostMobileTopBar} />}
      <MobileTopBarContainer.Root data-testid="writer-top-bar">
        {articleMeta && (
          <MobileTopBarContainer.Left>
            <CanView
              as={[UserRole.GUEST, UserRole.MEMBER, UserRole.SUPER_ADMIN, UserRole.ADMIN]}
              inWorkspace={articleMeta.company_id}
            >
              <MobileTopBarContainer.BackButton />
            </CanView>
            {isUnauthed && (
              <PermittedViewContainer articleMeta={articleMeta}>
                <AlmanacFileTitle id={articleMeta.id} assetType="Article" />
                {isPostable && scheduledPostId && articleMeta?.id && (
                  <ScheduledPostStatusTag scheduledPostId={scheduledPostId} articleId={articleMeta.id} />
                )}
              </PermittedViewContainer>
            )}
          </MobileTopBarContainer.Left>
        )}
        <MobileTopBarContainer.Center>
          {articleMeta && !isUnauthed && (
            <PermittedViewContainer articleMeta={articleMeta}>
              <AlmanacFileTitle id={articleMeta.id} assetType="Article" />
              {isPostable && articleMeta?.id && (
                <ScheduledPostStatusTag scheduledPostId={scheduledPostId} articleId={articleMeta.id} />
              )}
            </PermittedViewContainer>
          )}
        </MobileTopBarContainer.Center>
        <MobileTopBarContainer.Right>
          {articleMeta && (
            <PermittedViewContainer articleMeta={articleMeta}>
              <TopBarButton
                icon={MoreDots}
                label="File Menu"
                onPress={() =>
                  drawersStore.openDrawer(DocMenuDrawer, {
                    articleView: articleView || ArticleView.Reading,
                    articleId: articleMeta.id,
                    hiddenKeys: ['switchView', 'openInNewTab', 'rename', 'share'],
                  })
                }
              />
            </PermittedViewContainer>
          )}
          {articleMeta && (
            <CanView as={[UserRole.MEMBER, UserRole.SUPER_ADMIN, UserRole.ADMIN]} inWorkspace={articleMeta.company_id}>
              <ShareButtonButtonWithPlaceholder />
            </CanView>
          )}
          {docStore && !isPostable && (
            <CanView inWorkspace={workspace.id} inViewMode={ArticleView.Commenting}>
              <DocStoreContext.Provider value={docStore}>
                <CollaborationDrawerTrigger size={20} />
              </DocStoreContext.Provider>
            </CanView>
          )}

          {articleMeta && (
            <CanView as={[UserRole.UNAUTHED]} inWorkspace={articleMeta.company_id}>
              <TryBlazeButton />
            </CanView>
          )}
        </MobileTopBarContainer.Right>
      </MobileTopBarContainer.Root>
    </>
  )
})
